import {
    Button,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    GridSize,
    Radio,
    RadioGroup,
    TextField
} from "@material-ui/core";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";
import { documentOptions } from "../../constants";
import { t } from "../../i18n/util";
import { THEME } from "../../theme/ThemeStore";
import { DocumentsEnum } from "../../types/models";
import { FileError } from "../application/DataUploadForm";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { fileErrorToString, fromMegabytes, megabytes, optionToString } from "../util/helpers";
import { FormButtons, HrBotDialog, HrBotErrorMessage, HrBotOrangeMultipleButton } from "./Primitives";
import { UploadField } from "./UploadField";
import { floor } from "lodash";
import { LARGE_FILE_SIZE_MB } from "../../config";

type Props = {
    addFile(file: File): void;
    addLink(link: string): void;
    sm?: GridSize;
    selectedDocument?: DocumentsEnum;
    // TODO do not provide state setter, use proper on-handler instead
    setSelectedDocument?: React.Dispatch<React.SetStateAction<DocumentsEnum>>;
    style?: React.CSSProperties;
    type?: "application/pdf" | "image/jpeg" | "video/mp4" | "application/zip";
    remainingSize?: number;
};

export const AddField = ({
    addFile,
    addLink,
    sm,
    selectedDocument,
    setSelectedDocument,
    style,
    type,
    remainingSize
}: Props) => {
    const [open, setOpen] = React.useState(false);
    const [value, setValue] = React.useState<File | string>("");
    const [toggle, setToggle] = React.useState("file");

    const isMobile = useMobileMediaQuery();

    const [addFieldFileErrors, setAddFieldFileErrors] = React.useState<FileError[]>([]);

    const schema = yup.object().shape({
        link: yup.string().url(t("error.website")).required(t("error.missingLink"))
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setToggle((event.target as HTMLInputElement).value);
        setAddFieldFileErrors([]);
        setValue("");
    };

    const handleOpen = () => {
        setOpen(true);
        setToggle("file");
    };

    const handleClose = () => {
        setAddFieldFileErrors([]);
        setValue("");
        setToggle("file");
        setOpen(false);
    };

    const handleSelectDocument = (document: DocumentsEnum) => {
        if (setSelectedDocument) {
            setSelectedDocument(document);
        }
    };

    const handleSubmit = () => {
        if (toggle === "file") {
            if (!value) {
                if (!addFieldFileErrors.includes("required") && addFieldFileErrors.length === 0) {
                    setAddFieldFileErrors([...addFieldFileErrors, "required"]);
                }
            } else {
                addFile(value as File);
                handleClose();
            }
        } else if (toggle === "link") {
            schema
                .validate({ link: value })
                .then(function (valid) {
                    if (valid) {
                        addLink(value as string);
                        handleClose();
                    }
                })
                .catch(function (err) {
                    if (!addFieldFileErrors.includes(err.message)) {
                        setAddFieldFileErrors([err.message]);
                    }
                });
        }
    };

    const selectedDocumentToMimeType = () => {
        switch (selectedDocument) {
            case "video":
                return "video/mp4";
            case "photo":
                return "image/jpeg";
            default:
                return "application/pdf";
        }
    };

    let fileSize;
    if (remainingSize) {
        fileSize = remainingSize;
    } else {
        fileSize =
            selectedDocument === "video" || type === "application/zip" ? megabytes(LARGE_FILE_SIZE_MB) : undefined;
    }

    return (
        <>
            <Grid item xs={12} sm={sm ?? 6}>
                <Button
                    onClick={handleOpen}
                    style={{
                        height: 76,
                        border: `2px dashed ${THEME.customColors.dataUploadBorderColor}`,
                        borderRadius: 0,
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        padding: "0 0 0 22px",
                        ...style
                    }}
                >
                    <img
                        src={THEME.Images.icAddTransparent}
                        alt=""
                        style={{
                            width: 28,
                            height: 28,
                            marginRight: isMobile ? 10 : 16
                        }}
                    />
                    <div className="body1semibold">{t("screen.dataUpload.addFiles")}</div>
                </Button>
            </Grid>
            <HrBotDialog open={open}>
                <DialogTitle id="form-dialog-title" style={{ textAlign: "center", marginTop: 10 }} className="h3">
                    {t("screen.dataUpload.dialog.title")}
                </DialogTitle>
                <DialogContent>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 280,
                            justifyContent: "space-between"
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: isMobile ? "column" : "row",
                                    marginBottom: isMobile ? 16 : 0,
                                    justifyContent: "space-between"
                                }}
                            >
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            marginBottom: isMobile ? 0 : 16
                                        }}
                                        value={toggle}
                                        onChange={handleChange}
                                    >
                                        <FormControlLabel
                                            value="file"
                                            control={<Radio color="primary" />}
                                            className="body1semibold"
                                            label={t("screen.dataUpload.dialog.radio.file")}
                                        />
                                        <FormControlLabel
                                            value="link"
                                            control={<Radio color="primary" />}
                                            className="body1semibold"
                                            label={t("screen.dataUpload.dialog.radio.link")}
                                        />
                                    </RadioGroup>
                                </FormControl>
                                {selectedDocument && toggle === "file" && (
                                    <HrBotOrangeMultipleButton
                                        label={optionToString(documentOptions, selectedDocument)}
                                        options={documentOptions}
                                        onSelect={handleSelectDocument}
                                        dropdown
                                    />
                                )}
                            </div>

                            <div
                                style={{
                                    width: isMobile ? "100%" : 450,
                                    marginBottom: 24,
                                    display: "flex",
                                    flexDirection: "row"
                                }}
                            >
                                {toggle === "link" && (
                                    <TextField
                                        label={t("screen.dataUpload.dialog.radio.link")}
                                        onChange={(event: React.ChangeEvent<any>) => {
                                            schema
                                                .validate({ link: event.target.value })
                                                .then(function (valid) {
                                                    if (valid) {
                                                        setAddFieldFileErrors([]);
                                                    }
                                                })
                                                .catch(function (err) {
                                                    if (!addFieldFileErrors.includes(err.message)) {
                                                        setAddFieldFileErrors([err.message]);
                                                    }
                                                });
                                            setValue(event.target.value);
                                        }}
                                        InputProps={{ disableUnderline: true }}
                                        fullWidth
                                        style={{
                                            flexGrow: 1,
                                            minWidth: isMobile ? "60%" : "unset"
                                        }}
                                        color="secondary"
                                        variant="filled"
                                    />
                                )}
                                {toggle === "file" && (
                                    <UploadField
                                        name={t("screen.dataUpload.dialog.radio.file")}
                                        type={type ?? selectedDocumentToMimeType()}
                                        value={value as File}
                                        fileSize={fileSize}
                                        tooltip={
                                            selectedDocument === "video"
                                                ? t("screen.dataUpload.video.instructions")
                                                : undefined
                                        }
                                        onUploadFile={(currentFile) => {
                                            setValue(currentFile);
                                        }}
                                        onDelete={() => {
                                            setValue("");
                                        }}
                                        onSetFileErrors={(newFileErrors) => {
                                            setAddFieldFileErrors(newFileErrors);
                                        }}
                                        sm={12}
                                    />
                                )}
                            </div>
                            <div style={{ marginTop: 6 }}>
                                {addFieldFileErrors.map((fileError) => (
                                    <HrBotErrorMessage
                                        key={uuidv4()}
                                        label={fileErrorToString(
                                            type === "application/zip" ? "solution" : "optionalFile",
                                            fileError,
                                            remainingSize ? floor(fromMegabytes(remainingSize), 1) : undefined
                                        )}
                                    />
                                ))}
                            </div>
                        </div>
                        <FormButtons
                            labelBack={t("screen.dataUpload.dialog.cancel")}
                            labelSubmit={t("screen.dataUpload.dialog.upload")}
                            onBack={handleClose}
                            onSubmit={handleSubmit}
                            style={{ marginBottom: 0, justifyContent: "flex-end", padding: isMobile ? 0 : 10 }}
                        />
                    </div>
                </DialogContent>
            </HrBotDialog>
        </>
    );
};
